'use client';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Play, Shield, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';

// CAF Radar Visualization Component
function CAFRadarVisualization() {
  const objectives = [
    { id: 'A', name: 'Managing Security Risk', color: 'hsl(168 100% 45%)', principles: 4 },
    { id: 'B', name: 'Protecting Against Cyber Attack', color: 'hsl(225 60% 45%)', principles: 6 },
    { id: 'C', name: 'Detecting Cyber Security Events', color: 'hsl(38 92% 50%)', principles: 2 },
    { id: 'D', name: 'Minimising Impact', color: 'hsl(160 84% 39%)', principles: 2 },
  ];

  return (
    <div className="relative aspect-square w-full max-w-[520px]">
      {/* Outer glow */}
      <div className="absolute inset-0 rounded-full bg-accent/10 blur-3xl" />

      {/* Background grid */}
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 400 400">
        <defs>
          <radialGradient id="gridFade" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(220 13% 91%)" stopOpacity="0.5" />
            <stop offset="100%" stopColor="hsl(220 13% 91%)" stopOpacity="0" />
          </radialGradient>
        </defs>
        {[1, 2, 3, 4].map((i) => (
          <circle
            key={i}
            cx="200"
            cy="200"
            r={40 * i + 20}
            fill="none"
            stroke="url(#gridFade)"
            strokeWidth="1"
            className="opacity-60"
          />
        ))}
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i * 30 * Math.PI) / 180;
          return (
            <line
              key={i}
              x1="200"
              y1="200"
              x2={200 + Math.cos(angle) * 180}
              y2={200 + Math.sin(angle) * 180}
              stroke="url(#gridFade)"
              strokeWidth="1"
              className="opacity-40"
            />
          );
        })}
      </svg>

      {/* Main visualization */}
      <svg className="relative h-full w-full" viewBox="0 0 400 400">
        <defs>
          <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(168 100% 45%)" />
            <stop offset="50%" stopColor="hsl(225 60% 45%)" />
            <stop offset="100%" stopColor="hsl(38 92% 50%)" />
          </linearGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="pulse" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {objectives.map((obj, index) => {
          const startAngle = (index * 90 - 90) * (Math.PI / 180);
          const endAngle = ((index + 1) * 90 - 90) * (Math.PI / 180);
          const radius = 140;
          const x1 = 200 + Math.cos(startAngle) * radius;
          const y1 = 200 + Math.sin(startAngle) * radius;
          const x2 = 200 + Math.cos(endAngle) * radius;
          const y2 = 200 + Math.sin(endAngle) * radius;

          return (
            <motion.g key={obj.id}>
              <motion.path
                d={`M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`}
                fill="none"
                stroke={obj.color}
                strokeWidth="24"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1.5, delay: index * 0.2, ease: [0.22, 1, 0.36, 1] }}
                filter="url(#glow)"
                className="opacity-80"
              />
              {Array.from({ length: obj.principles }).map((_, pi) => {
                const nodeAngle =
                  startAngle + ((endAngle - startAngle) * (pi + 0.5)) / obj.principles;
                const nodeRadius = 140;
                const nx = 200 + Math.cos(nodeAngle) * nodeRadius;
                const ny = 200 + Math.sin(nodeAngle) * nodeRadius;
                const isComplete = Math.random() > 0.3;

                return (
                  <motion.g key={`${obj.id}-${pi}`}>
                    <motion.circle
                      cx={nx}
                      cy={ny}
                      r="8"
                      fill={isComplete ? obj.color : 'hsl(40 20% 98%)'}
                      stroke={obj.color}
                      strokeWidth="2"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.4, delay: 0.8 + index * 0.2 + pi * 0.1, ease: 'backOut' }}
                      filter="url(#pulse)"
                    />
                    {isComplete && (
                      <motion.circle
                        cx={nx}
                        cy={ny}
                        r="12"
                        fill="none"
                        stroke={obj.color}
                        strokeWidth="1"
                        initial={{ scale: 0.5, opacity: 1 }}
                        animate={{ scale: 2, opacity: 0 }}
                        transition={{ duration: 2, repeat: Infinity, delay: pi * 0.3 }}
                      />
                    )}
                  </motion.g>
                );
              })}
            </motion.g>
          );
        })}

        {/* Center shield */}
        <motion.g
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3, ease: 'backOut' }}
        >
          <circle cx="200" cy="200" r="50" fill="hsl(225 60% 8%)" className="drop-shadow-xl" />
          <circle
            cx="200"
            cy="200"
            r="42"
            fill="none"
            stroke="hsl(168 100% 45%)"
            strokeWidth="2"
            strokeDasharray="4 4"
            className="animate-spin-slow"
          />
        </motion.g>
        <motion.g
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.6, type: 'spring' }}
        >
          <foreignObject x="175" y="175" width="50" height="50">
            <div className="flex h-full w-full items-center justify-center">
              <Shield className="h-7 w-7 text-white" strokeWidth={1.5} />
            </div>
          </foreignObject>
        </motion.g>

        {/* Connection lines */}
        {objectives.map((obj, index) => {
          const angle = (index * 90 + 45 - 90) * (Math.PI / 180);
          const innerRadius = 55;
          const outerRadius = 115;
          const lx1 = 200 + Math.cos(angle) * innerRadius;
          const ly1 = 200 + Math.sin(angle) * innerRadius;
          const lx2 = 200 + Math.cos(angle) * outerRadius;
          const ly2 = 200 + Math.sin(angle) * outerRadius;
          return (
            <motion.line
              key={`line-${obj.id}`}
              x1={lx1}
              y1={ly1}
              x2={lx2}
              y2={ly2}
              stroke={obj.color}
              strokeWidth="2"
              strokeDasharray="4 2"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.6 }}
              transition={{ duration: 0.8, delay: 1 + index * 0.1 }}
            />
          );
        })}

        {/* Objective labels */}
        {objectives.map((obj, index) => {
          const angle = (index * 90 + 45 - 90) * (Math.PI / 180);
          const radius = 175;
          const lx = 200 + Math.cos(angle) * radius;
          const ly = 200 + Math.sin(angle) * radius;
          return (
            <motion.g
              key={`label-${obj.id}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.5 + index * 0.1 }}
            >
              <rect x={lx - 16} y={ly - 12} width="32" height="24" rx="6" fill="white" className="drop-shadow-md" />
              <text x={lx} y={ly + 5} textAnchor="middle" className="fill-primary font-heading text-sm font-bold">
                {obj.id}
              </text>
            </motion.g>
          );
        })}
      </svg>

      {/* Floating stats */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 2 }}
        className="hidden sm:block absolute -right-4 top-1/4 rounded-xl border border-border bg-card p-3 shadow-lg sm:-right-8"
      >
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-success/10">
            <CheckCircle2 className="h-4 w-4 text-success" />
          </div>
          <div>
            <p className="font-heading text-lg font-bold text-foreground">87%</p>
            <p className="text-xs text-muted-foreground">Complete</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 2.2 }}
        className="hidden sm:block absolute -left-4 bottom-1/4 rounded-xl border border-border bg-card p-3 shadow-lg sm:-left-8"
      >
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10">
            <span className="font-heading text-sm font-bold text-accent">41</span>
          </div>
          <div>
            <p className="font-heading text-sm font-semibold text-foreground">COs</p>
            <p className="text-xs text-muted-foreground">Tracked</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function HeroSection() {
  return (
    <section className="relative min-h-screen overflow-hidden bg-hero-gradient pt-20 lg:pt-24">
      <div className="bg-mesh-1 absolute inset-0" />
      <div className="bg-grid absolute inset-0 opacity-50" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid min-h-[calc(100vh-5rem)] items-center gap-12 py-12 lg:grid-cols-2 lg:gap-8 lg:py-0">
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center text-center lg:items-start lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Badge
                variant="secondary"
                className="mb-6 gap-2 border border-accent/20 bg-accent/5 px-4 py-1.5 font-heading text-sm font-medium text-foreground"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
                </span>
                Built around CAF 4.0
              </Badge>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="font-display text-3xl tracking-tight-display text-foreground sm:text-4xl md:text-5xl lg:text-6xl xl:text-[64px]"
            >
              CAF 4.0 compliance,{' '}
              <span className="italic text-accent">made measurable.</span>
            </motion.h1>

            {/* Subheadline — one sentence */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl"
            >
              CAF-Pilot operationalises the NCSC Cyber Assessment Framework for UK
              Critical National Infrastructure — structured scoring, evidence mapped
              to controls, and audit trails that turn months of assessment into weeks.
            </motion.p>

            {/* Stat row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 lg:justify-start"
            >
              {[
                { value: '4', label: 'Objectives' },
                { value: '14', label: 'Principles' },
                { value: '41', label: 'Contributing Outcomes' },
                { value: '~300', label: 'IGPs' },
              ].map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <p className="font-heading text-2xl font-bold text-foreground">{stat.value}</p>
                  <p className="text-xs uppercase tracking-wide-caps text-muted-foreground">
                    {stat.label}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-10 flex flex-col items-center gap-4 sm:flex-row lg:items-start"
            >
              <Button
                size="lg"
                className="btn-glow h-12 bg-accent px-6 font-heading text-base font-semibold text-accent-foreground hover:bg-accent/90"
                asChild
              >
                <Link href="/register">
                  Start your 7-day free trial
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-12 border-border/60 px-6 font-heading text-base font-medium hover:bg-secondary"
                asChild
              >
                <a href="#about">
                  <Play className="mr-2 h-4 w-4" />
                  Watch demo
                </a>
              </Button>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-8 text-sm text-muted-foreground"
            >
              7-day free trial · no credit card required · setup in minutes
            </motion.p>
          </motion.div>

          {/* Right Column */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center justify-center lg:justify-end"
          >
            <CAFRadarVisualization />
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
}
